import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UserGroupMark from './UserGroupMark';
import { GLOBAL_GROUP_LABEL } from './userGroupConstants';
import { CONSTANTS } from 'src/components/Constants';

// Dropdown anchored to the chip. Renders one row per group the user belongs
// to, with the active group flagged by a tick. The per-row star toggles
// the "default at login" affordance preserved from the legacy switcher.
const UserGroupSwitcherMenu = ({
  anchorEl,
  open,
  onClose,
  groups,
  currentGroupId,
  defaultGroupId,
  accentColor,
  onSwitch,
  onSetDefault,
  onClearDefault
}) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            width: 308,
            maxHeight: 'calc(100vh - 120px)',
            borderRadius: '10px',
            border: '0.5px solid rgba(0,0,0,0.1)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.14)',
            overflow: 'hidden'
          }
        },

        list: { sx: { py: 0 } }
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: '#6B7280'
        }}
      >
        Switch User Group
      </Box>
      <Box sx={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
        {(groups || []).map((g) => {
          const groupId = g.userGroup?._id?.toString();
          if (!groupId) return null;
          const isCurrent = groupId === currentGroupId;
          const isDefault = groupId === defaultGroupId;
          const roleLabel =
            CONSTANTS.userTypeOptionsMap?.[g.userType] || g.userType || '';
          // The System Admin group has no name (TGCLOUD-1858).
          const isGlobal =
            g.userType === CONSTANTS.userTypes.ADMIN && !g.userGroup?.label;
          const groupLabel = isGlobal ? GLOBAL_GROUP_LABEL : g.userGroup?.label;
          const groupForMark = {
            _id: groupId,
            label: groupLabel,
            global: isGlobal,
            faviconUrl: g.userGroup?.faviconUrl || null
          };

          return (
            <MenuItem
              key={groupId}
              onClick={() => onSwitch(groupId, isCurrent)}
              sx={{
                py: '9px',
                px: 2,
                gap: 1.5,
                bgcolor: isCurrent ? 'rgba(43, 169, 191, 0.07)' : 'transparent',
                '&:hover': {
                  bgcolor: isCurrent ? 'rgba(43, 169, 191, 0.10)' : '#F5F7FA'
                }
              }}
            >
              <UserGroupMark userGroup={groupForMark} size="md" />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#1a1a1a',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {groupLabel}
                </Box>
                {/* Wraps: a MenuItem is nowrap, so "Default at login ·
                    <role>" otherwise ran on under the star button. */}
                <Box
                  data-testid="group-row-subtitle"
                  sx={{
                    fontSize: 11,
                    color: '#6B7280',
                    mt: 0.25,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    columnGap: 0.5,
                    rowGap: 0.25,
                    minWidth: 0
                  }}
                >
                  {isDefault ? (
                    <>
                      <StarIcon
                        aria-hidden="true"
                        sx={{ fontSize: 12, color: accentColor }}
                      />
                      <Box component="span">Default at login</Box>
                      {roleLabel && (
                        <Box component="span" sx={{ color: '#9CA3AF' }}>
                          · {roleLabel}
                        </Box>
                      )}
                    </>
                  ) : (
                    roleLabel
                  )}
                </Box>
              </Box>

              <Tooltip
                title={isDefault ? 'Remove default' : 'Set as default at login'}
              >
                <IconButton
                  size="small"
                  aria-label={
                    isDefault
                      ? 'Remove default User Group'
                      : 'Set as default User Group at login'
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isDefault) onClearDefault();
                    else onSetDefault(groupId);
                  }}
                >
                  {isDefault ? (
                    <StarIcon
                      fontSize="small"
                      sx={{ color: accentColor || '#2BA9BF' }}
                    />
                  ) : (
                    <StarBorderIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              {isCurrent && (
                <CheckIcon
                  aria-hidden="true"
                  sx={{ fontSize: 18, color: '#2BA9BF', ml: 0.5 }}
                />
              )}
            </MenuItem>
          );
        })}
      </Box>
    </Menu>
  );
};

export default UserGroupSwitcherMenu;
